<template>
  <div
    v-clickoutside="handleClose"
    :class="[selectSize ? 'el-select--' + selectSize : '']"
    class="el-select"
    @click.stop="toggleMenu"
  >
    <div
      v-if="multiple"
      ref="tagsRef"
      :style="{
        'max-width': inputWidth - 32 + 'px',
        width: '100%',
        opacity: 0,
        'pointer-events': 'none'
      }"
      class="el-select__tags"
    >
      <span v-if="collapseTags && selected.length">
        <rt-tag
          v-for="(item, index) in selected"
          :key="index"
          size="medium"
          :disabled="computedDisabled"
          :closable="!computedDisabled"
          class="rt-tag-box"
          type="fill"
        >{{ item.currentLabel }}
        </rt-tag>
      </span>
    </div>
    <div
      v-if="multiple"
      ref="tags"
      :style="{
        'max-width': inputWidth - 32 + 'px',
        width: '100%',
        'flex-wrap': 'nowrap'
      }"
      class="el-select__tags"
      @mouseenter="multipleTagsMouseenterHandler"
      @mouseleave="multipleTagsMouseleaveHandler"
    >
      <span v-if="collapseTags && selected.length">
        <rt-tag
          v-for="(item, index) in selected.slice(0, maxSelectedNum)"
          ref="multipleTag"
          :key="index"
          :disabled="computedDisabled"
          :closable="!computedDisabled"
          type="fill"
          size="medium"
          @close="deleteTag($event, item)"
        >
          {{ item.currentLabel }}
        </rt-tag>

        <rt-tooltip
          v-if="selected.length > maxSelectedNum"
          :content="maxSelectedContent"
          placement="bottom"
        >
          <rt-tag
            :disabled="computedDisabled"
            :closable="false"
            type="fill"
            size="medium"
          >
            + {{ selected.length - maxSelectedNum }}
          </rt-tag>
        </rt-tooltip>
      </span>
      <transition-group
        v-if="!collapseTags"
        @after-leave="resetInputHeight"
      >
        <rt-tag
          v-for="item in selected"
          :key="getValueKey(item)"
          :disabled="computedDisabled"
          :closable="!computedDisabled"
          type="fill"
          size="medium"
          @close="deleteTag($event, item)"
        >
          {{ item[alias.name] }}
        </rt-tag>
      </transition-group>

      <input
        v-if="filterable"
        ref="input"
        v-model="query"
        :class="[selectSize ? `is-${selectSize}` : '']"
        :disabled="selectDisabled"
        :autocomplete="autoComplete || autocomplete"
        :style="{
          'flex-grow': '1',
          width: inputLength / (inputWidth - 32) + '%',
          'max-width': inputWidth - 42 + 'px',
          border: 'none',
          paddingLeft: 0,
        }"
        type="text"
        class="el-select__input"
        @focus="handleFocus"
        @blur="softFocus = false"
        @click.stop
        @keyup="managePlaceholder"
        @keydown="resetInputState"
        @keydown.down.prevent="navigateOptions('next')"
        @keydown.up.prevent="navigateOptions('prev')"
        @keydown.enter.prevent="selectOption"
        @keydown.esc.stop.prevent="visible = false"
        @keydown.delete="deletePrevTag"
        @compositionstart="handleComposition"
        @compositionupdate="handleComposition"
        @compositionend="handleComposition"
        @input="debouncedQueryChange"
      />
    </div>
    <!--下拉列表单选后回显的样式
        32355【rtSelect】选择器选项展示方式默认为rtTag
        2022/10/8 调整【单选 选择器】的样式，其他不变
    -->
    <div
      v-else-if="isPicklist"
      class="el-select__tags"
      :style="{
        'max-width': inputWidth - 32 + 'px',
        width: '100%',
        'flex-wrap': 'nowrap',
      }"
      @mouseenter="multipleTagsMouseenterHandler"
      @mouseleave="multipleTagsMouseleaveHandler"
    >
      <rt-tag
        v-if="computedNonEmptyTagValue"
        ref="singleTag"
        type="fill"
        size="medium"
        :max-width="inputWidth - 44"
      >
        {{ tagValue }}
      </rt-tag>
      <input
        v-if="computedNonEmptyTagValue && filterable"
        v-model="radioValue"
        :class="[selectSize ? `is-${selectSize}` : '']"
        :autocomplete="autoComplete || autocomplete"
        :disabled="selectDisabled"
        :style="{
          'flex-grow': '1',
          width: inputLength / (inputWidth - 32) + '%',
          'max-width': inputWidth - 42 + 'px',
          border: 'none',
          paddingLeft: 0,
          minWidth: 16 + 'px',
        }"
        type="text"
        class="el-select__input"
        @focus="handleFocus"
        @click.stop
        @keydown.down.prevent="navigateOptions('next')"
        @keydown.up.prevent="navigateOptions('prev')"
        @keydown.enter.prevent="selectOption"
        @keydown.esc.stop.prevent="visible = false"
        @keydown.delete="deletePrevTag"
        @blur="handleRadioBlur"
      />
    </div>
    <el-input
      :id="id"
      ref="reference"
      v-model="selectedLabel"
      :placeholder="_computedPlaceholder"
      :name="name"
      :autocomplete="autoComplete || autocomplete"
      :size="selectSize"
      :disabled="selectDisabled"
      :readonly="readonly"
      :validate-event="false"
      :class="{ 'is-focus': visible,'is-hover' : inputIsHover }"
      type="text"
      @focus="handleFocus"
      @blur="handleBlur"
      @keyup.native="debouncedOnInputChange"
      @keydown.native="handleKeydown"
      @keydown.native.down.stop.prevent="navigateOptions('next')"
      @keydown.native.up.stop.prevent="navigateOptions('prev')"
      @keydown.native.enter.prevent="selectOption"
      @keydown.native.esc.stop.prevent="visible = false"
      @keydown.native.tab="visible = false"
      @paste.native="debouncedOnInputChange"
      @mouseenter.native="inputHovering = true"
      @mouseleave.native="inputHovering = false"
    >
      <template
        v-if="$slots.prefix"
        slot="prefix"
      >
        <slot name="prefix"></slot>
      </template>
      <template slot="suffix">
        <i
          v-show="!showClose"
          :class="[
            'el-select__caret',
            'el-input__icon',
            'el-icon-' + iconClass
          ]"
        ></i>
        <i
          v-if="showClose"
          class="el-select__caret el-input__icon el-icon-circle-close"
          @click="handleClearClick"
        ></i>
      </template>
    </el-input>
    <transition
      name="el-zoom-in-top"
      @before-enter="handleMenuEnter"
      @after-leave="doDestroy"
    >
      <el-select-menu
        v-show="visible && emptyText !== false"
        ref="popper"
        :append-to-body="popperAppendToBody"
      >
        <el-scrollbar
          v-show="options.length > 0 && !loading"
          ref="scrollbar"
          :class="{
            'is-empty': !allowCreate && query && filteredOptionsCount === 0
          }"
          tag="ul"
          wrap-class="el-select-dropdown__wrap"
          view-class="el-select-dropdown__list"
        >
          <el-option
            v-if="showNewOption"
            :value="query"
            created
          >
          </el-option>
          <slot></slot>
        </el-scrollbar>
        <template
          v-if="
            emptyText &&
              (!allowCreate || loading || (allowCreate && options.length === 0))
          "
        >
          <slot
            v-if="$slots.empty"
            name="empty"
          ></slot>
          <p
            v-else
            class="el-select-dropdown__empty"
          >
            {{ emptyText }}
          </p>
        </template>
      </el-select-menu>
    </transition>
  </div>
</template>

<script type="text/babel">
import Emitter from 'element-ui/lib/mixins/emitter';
import Focus from 'element-ui/lib/mixins/focus';
import Locale from 'element-ui/lib/mixins/locale';
import ElInput from '../../input/src/input.vue';
import ElSelectMenu from './select-dropdown.vue';
import ElOption from './option.vue';
import ElScrollbar from '../../scrollbar';
import debounce from 'throttle-debounce/debounce';
import Clickoutside from 'element-ui/lib/utils/clickoutside';
import { t } from 'element-ui/lib/locale';
import scrollIntoView from 'element-ui/lib/utils/scroll-into-view';
import RtTooltip from '../../../../components/tooltip/rtTooltip.vue'
import {
  getValueByPath,
  valueEquals,
  isIE,
  isEdge
} from 'element-ui/lib/utils/util';

import NavigationMixin from './navigation-mixin';
import { isKorean } from 'element-ui/lib/utils/shared';
import _ from 'lodash';
import { isNilOrEmptyString } from 'vue-xpc/src/utils/util';

export default {
  name: 'ElSelect',

  components: {
    ElInput,
    ElSelectMenu,
    ElOption,
    ElScrollbar,
    RtTooltip
  },

  directives: { Clickoutside },

  mixins: [Emitter, Locale, Focus('reference'), NavigationMixin],

  componentName: 'ElSelect',

  inject: {
    elForm: {
      default: ''
    },

    elFormItem: {
      default: ''
    }
  },

  provide () {
    return {
      select: this
    };
  },

  props: {
    value: {
      type: [String, Array, Number, Object],
      default: null
    },
    name: String,
    id: String,
    autocomplete: {
      type: String,
      default: 'off'
    },
    /** @Deprecated in next major version */
    autoComplete: {
      type: String,
      validator (val) {
        process.env.NODE_ENV !== 'production' &&
          console.warn(
            "[Element Warn][Select]'auto-complete' property will be deprecated in next major version. please use 'autocomplete' instead."
          );
        return true;
      }
    },
    automaticDropdown: Boolean,
    size: String,
    disabled: Boolean,
    clearable: { type: Boolean, default: undefined },
    filterable: Boolean,
    allowCreate: Boolean,
    loading: Boolean,
    popperClass: String,
    remote: Boolean,
    loadingText: String,
    noMatchText: String,
    noDataText: String,
    remoteMethod: Function,
    filterMethod: Function,
    isPicklist: Boolean,
    multiple: Boolean,
    multipleLimit: {
      type: Number,
      default: 0
    },
    placeholder: {
      type: String,
      default () {
        return t('el.select.placeholder');
      }
    },
    defaultFirstOption: Boolean,
    reserveKeyword: Boolean,
    valueKey: {
      type: String,
      default: 'value'
    },
    collapseTags: Boolean,
    popperAppendToBody: {
      type: Boolean,
      default: true
    },
    // 最多显示多少个已选标签
    showMaxSelected: {
      type: Number,
      default: null
    }
  },

  data () {
    return {
      options: [],
      cachedOptions: [],
      createdLabel: null,
      createdSelected: false,
      selected: this.multiple ? [] : {},
      inputLength: 20,
      inputWidth: 0,
      initialInputHeight: 0,
      cachedPlaceHolder: '',
      optionsCount: 0,
      filteredOptionsCount: 0,
      visible: false,
      softFocus: false,
      selectedLabel: '',
      radioValue: '',
      tagValue: null,
      hoverIndex: -1,
      query: '',
      previousQuery: null,
      inputHovering: false,
      currentPlaceholder: '',
      menuVisibleOnFocus: false,
      isOnComposition: false,
      isSilentBlur: false,
      maxIndex: 0,
      inputIsHover: false,
      maxSelectedContent: '', // +n 的 tooltip 提示语
      hasFiltered: false // 是否已经过滤
    };
  },

  computed: {
    computedClearable () {
      return this.clearable === undefined
        ? this.elForm && this.elForm.$attrs && this.elForm.$attrs['all-clearable']
        : this.clearable;
    },
    // 问题【如果placeholder设置的长一些，选的tag就遮不住提示语】
    // 修复【tag存在的时候，placeholder置空】
    _computedPlaceholder () {
      return ((!isNilOrEmptyString(this.tagValue) && this.isPicklist) || this.selected.length > 0) ? '' : this.currentPlaceholder;
    },

    _elFormItemSize () {
      return (this.elFormItem || {}).elFormItemSize;
    },

    computedNonEmptyTagValue () {
      return !isNilOrEmptyString(this.tagValue);
    },

    readonly () {
      return (
        !this.filterable ||
        this.multiple ||
        (!isIE() && !isEdge() && !this.visible) ||
        (this.isPicklist && this.filterable && !isNilOrEmptyString(this.tagValue))
      );
    },

    showClose () {
      let hasValue = this.multiple
        ? Array.isArray(this.value) && this.value.length > 0
        : this.value !== undefined && this.value !== null && this.value !== '';
      return (
        this.computedClearable &&
        !this.selectDisabled &&
        this.inputHovering &&
        hasValue
      );
    },

    iconClass () {
      if (this.remote && this.filterable) {
        return '';
      } else if (this.visible) {
        return 'arrow-up is-reverse';
      }
      return 'arrow-up';
    },

    debounce () {
      return this.remote ? 300 : 0;
    },

    emptyText () {
      if (this.loading) {
        return this.loadingText || this.t('el.select.loading');
      } else {
        if (this.remote && this.query === '' && this.options.length === 0) {
          return false;
        }
        if (
          this.filterable &&
          this.query &&
          this.options.length > 0 &&
          this.filteredOptionsCount === 0
        ) {
          return this.noMatchText || this.t('el.select.noMatch');
        }
        if (this.options.length === 0) {
          return this.noDataText || this.t('el.select.noData');
        }
      }
      return null;
    },

    showNewOption () {
      let hasExistingOption = this.options
        .filter(option => !option.created)
        .some(option => option.currentLabel === this.query);
      return (
        this.filterable &&
        this.allowCreate &&
        this.query !== '' &&
        !hasExistingOption
      );
    },

    selectSize () {
      return this.size || this._elFormItemSize || (this.$ELEMENT || {}).size;
    },

    selectDisabled () {
      return this.disabled || (this.elForm || {}).disabled;
    },

    collapseTagSize () {
      return ['small', 'mini'].indexOf(this.selectSize) > -1 ? 'mini' : 'small';
    },

    maxSelectedNum () {
      return this.showMaxSelected ? this.showMaxSelected : this.maxIndex + 1;
    },

    computedDisabled () {
      return this.disabled || (this.elForm || {}).disabled;
    }
  },
  watch: {
    selected (val) {
      if (this.multiple) {
        this.$nextTick(() => {
          const tagList = Array.from(
            this.$refs.tagsRef &&
              this.$refs.tagsRef.getElementsByClassName('rt-tag-box')
          );
          let width = 0;
          let _maxIndex = 0;
          for (let i = 0; i < tagList.length; i++) {
            width += tagList[i].offsetWidth;
            if (width + 8 * i > this.inputWidth - 100) {
              break;
            }
            _maxIndex = i;
          }
          this.maxIndex = _maxIndex;
          this.showMaxSelectedTipContent();
        });
      }
    },
    inputWidth (val) {
      if (this.multiple) {
        this.$nextTick(() => {
          const tagList = Array.from(
            this.$refs.tagsRef &&
              this.$refs.tagsRef.getElementsByClassName('rt-tag-box')
          );
          let width = 0;
          let _maxIndex = 0;
          for (let i = 0; i < tagList.length; i++) {
            width += tagList[i].offsetWidth;
            if (width + 8 * i > val - 100) {
              break;
            }
            _maxIndex = i;
          }
          this.maxIndex = _maxIndex;
          this.showMaxSelectedTipContent();
        });
      }
    },

    selectDisabled () {
      this.$nextTick(() => {
        this.resetInputHeight();
      });
    },

    placeholder (val) {
      this.cachedPlaceHolder = this.currentPlaceholder = val;
    },

    value (val, oldVal) {
      if (this.multiple) {
        this.resetInputHeight();
        if (val.length > 0 || (this.$refs.input && this.query !== '')) {
          this.currentPlaceholder = '';
        } else {
          this.currentPlaceholder = this.cachedPlaceHolder;
        }
        if (this.filterable && !this.reserveKeyword) {
          this.query = '';
          this.handleQueryChange(this.query);
        }
      }
      this.setSelected();
      if (this.filterable && !this.multiple) {
        this.inputLength = 20;
      }
      if (!valueEquals(val, oldVal)) {
        this.dispatch('ElFormItem', 'el.form.change', val);
      }
    },
    // 每次都会监听options列表弹出和关闭
    visible (val) {
      // 关闭弹窗options
      if (!val) {
        this.broadcast('ElSelectDropdown', 'destroyPopper');
        if (this.$refs.input) {
          this.$refs.input.blur();
        }
        this.query = '';
        this.previousQuery = null;
        this.clearInputValue();
        this.inputLength = 20;
        this.menuVisibleOnFocus = false;
        this.resetHoverIndex();
        this.$nextTick(() => {
          if (
            this.$refs.input &&
            this.$refs.input.value === '' &&
            this.selected.length === 0
          ) {
            this.currentPlaceholder = this.cachedPlaceHolder;
          }
        });
        if (!this.multiple) {
          if (this.selected) {
            if (
              this.filterable &&
                this.allowCreate &&
                this.createdSelected &&
                this.createdLabel
            ) {
              this.selectedLabel = this.createdLabel;
            } else {
              this.selectedLabel = this.selected.currentLabel;
            }
            if (this.filterable) this.query = this.selectedLabel;
          }

          if (this.filterable) {
            this.currentPlaceholder = this.cachedPlaceHolder;
          }
        }
        // 开启弹窗options
      } else {
        this.broadcast('ElSelectDropdown', 'updatePopper');
        if (this.filterable) {
          this.query = '';
          this.handleQueryChange(this.query);
          if (this.multiple) {
            this.$refs.input.focus();
          } else {
            if (!this.remote) {
              this.broadcast('ElOption', 'queryChange', '');
              this.broadcast('ElOptionGroup', 'queryChange');
            }
            if (!isNilOrEmptyString(this.selectedLabel)) {
              this.currentPlaceholder = this.selectedLabel;
              this.clearInputValue();
            }
          }
        }
      }
      this.$emit('visible-change', val);
    },

    options () {
      if (this.$isServer) return;
      this.$nextTick(() => {
        this.broadcast('ElSelectDropdown', 'updatePopper');
      });
      if (this.multiple) {
        this.resetInputHeight();
      }
      let inputs = this.$el.querySelectorAll('input');
      if ([].indexOf.call(inputs, document.activeElement) === -1) {
        this.setSelected();
      }
      if (
        this.defaultFirstOption &&
        (this.filterable || this.remote) &&
        this.filteredOptionsCount
      ) {
        this.checkDefaultFirstOption();
      }
    },

    radioValue (val) {
      if (this.visible) {
        this.handleQueryChange(val);
      }
      // this.visible = true;
    }
  },

  created () {
    this.cachedPlaceHolder = this.currentPlaceholder = this.placeholder;
    if (this.multiple && !Array.isArray(this.value)) {
      this.$emit('input', []);
    }
    if (!this.multiple && Array.isArray(this.value)) {
      this.$emit('input', '');
    }

    this.debouncedOnInputChange = debounce(this.debounce, () => {
      this.onInputChange();
    });

    this.debouncedQueryChange = debounce(this.debounce, e => {
      this.handleQueryChange(e.target.value);
    });

    this.$on('handleOptionClick', this.handleOptionSelect);
    this.$on('setSelected', this.setSelected);
  },

  mounted () {
    if (this.multiple && Array.isArray(this.value) && this.value.length > 0) {
      this.currentPlaceholder = '';
    }

    const reference = this.$refs.reference;
    if (reference && reference.$el) {
      const sizeMap = {
        medium: 36,
        small: 32,
        mini: 28
      };
      this.$setTimeout(() => {
        this.initialInputHeight =
        reference.$el.getBoundingClientRect().height ||
        sizeMap[this.selectSize];
      }, 300);
    }
    if (this.remote && this.multiple) {
      this.$setTimeout(() => {
        this.resetInputHeight();
      }, 300);
    }
    this.$nextTick(() => {
      if (reference && reference.$el && !this.inputSizeObserver) {
        this.inputSizeObserver = new ResizeObserver(
          _.throttle(entries => {
            entries.forEach(entry => {
              this.inputWidth = entry.contentRect.width;
              if (this.multiple) this.resetInputHeight();
            });
          }),
          500
        );
        this.inputSizeObserver.observe(reference.$el);
      }

      this.showMaxSelectedTipContent();
    });
    this.setSelected();
  },

  beforeDestroy () {
    if (this.inputSizeObserver) this.inputSizeObserver.disconnect();
  },

  methods: {
    /**
     * tag 存在时，elInput 置空
     */
    clearInputValue () {
      this.selectedLabel = '';
    },
    showMaxSelectedTipContent () {
      let str = '';
      if (this.selected && this.selected.length > 0) {
        this.selected.slice(this.maxSelectedNum).forEach(item => {
          str += item['currentLabel'] + ';';
        });
        str = str.substring(0, str.length - 1);
        this.maxSelectedContent = str;
      }
    },
    handleKeydown (event) {
      let keyCode = event.keyCode;
      if (keyCode === 37 || keyCode === 39) {
        this.visible = true;
        event.stopPropagation();
        event.preventDefault();
      }
    },
    handleComposition (event) {
      const text = event.target.value;
      if (event.type === 'compositionend') {
        this.isOnComposition = false;
        this.handleQueryChange(text);
      } else {
        const lastCharacter = text[text.length - 1] || '';
        this.isOnComposition = !isKorean(lastCharacter);
      }
    },
    handleQueryChange (val) {
      if (this.previousQuery === val || this.isOnComposition) return;
      if (
        this.previousQuery === null &&
        (typeof this.filterMethod === 'function' ||
          typeof this.remoteMethod === 'function')
      ) {
        this.previousQuery = val;
        if (!this.hasFiltered) {
          return;
        }
      }
      this.previousQuery = val;

      this.hasFiltered = !!val;
      this.$nextTick(() => {
        if (this.visible) this.broadcast('ElSelectDropdown', 'updatePopper');
      });
      this.hoverIndex = -1;
      if (this.multiple && this.filterable) {
        const length = this.$refs.input.value.length * 15 + 20;
        this.inputLength = this.collapseTags ? Math.min(50, length) : length;
        this.managePlaceholder();
        this.resetInputHeight();
      }
      if (this.remote && typeof this.remoteMethod === 'function') {
        this.hoverIndex = -1;
        this.remoteMethod(val);
      } else if (typeof this.filterMethod === 'function') {
        this.filterMethod(val);
        this.filteredOptionsCount = this.optionsCount;
        this.broadcast('ElOption', 'queryChange', val);
        this.broadcast('ElOptionGroup', 'queryChange');
      } else {
        this.filteredOptionsCount = this.optionsCount;
        this.broadcast('ElOption', 'queryChange', val);
        this.broadcast('ElOptionGroup', 'queryChange');
      }
      if (
        this.defaultFirstOption &&
        (this.filterable || this.remote) &&
        this.filteredOptionsCount
      ) {
        this.checkDefaultFirstOption();
      }
    },

    scrollToOption (option) {
      const target =
        Array.isArray(option) && option[0] ? option[0].$el : option.$el;
      if (this.$refs.popper && target) {
        const menu = this.$refs.popper.$el.querySelector(
          '.el-select-dropdown__wrap'
        );
        scrollIntoView(menu, target);
      }
      this.$refs.scrollbar && this.$refs.scrollbar.handleScroll();
    },

    handleMenuEnter () {
      this.$nextTick(() => this.scrollToOption(this.selected));
    },

    emitChange (val) {
      if (!valueEquals(this.value, val)) {
        this.$emit('change', val);
      }
    },

    getOption (value) {
      let option;
      const isObject =
        Object.prototype.toString.call(value).toLowerCase() ===
        '[object object]';
      const isNull =
        Object.prototype.toString.call(value).toLowerCase() === '[object null]';

      for (let i = this.cachedOptions.length - 1; i >= 0; i--) {
        const cachedOption = this.cachedOptions[i];
        const isEqual = isObject
          ? getValueByPath(cachedOption.value, this.valueKey) ===
            getValueByPath(value, this.valueKey)
          : cachedOption.value === value;
        if (isEqual) {
          option = cachedOption;
          break;
        }
      }
      if (option) return option;
      const label = !isObject && !isNull ? value : '';
      let newOption = {
        value: value,
        currentLabel: label
      };
      if (this.multiple) {
        newOption.hitState = false;
      }
      return newOption;
    },
    // 设置初始化值
    setSelected () {
      if (!this.multiple) {
        let option = this.getOption(this.value);
        if (option.created) {
          this.createdLabel = option.currentLabel;
          this.createdSelected = true;
        } else {
          this.createdSelected = false;
        }
        this.selectedLabel = option.currentLabel;

        this.tagValue = option.currentLabel;
        this.selected = option;
        if (this.isPicklist) this.clearInputValue();
        if (this.filterable) this.query = this.selectedLabel;
        return;
      }
      let result = [];
      if (Array.isArray(this.value)) {
        this.value.forEach(value => {
          result.push(this.getOption(value));
        });
      }
      this.selected = result;
      this.$nextTick(() => {
        this.resetInputHeight();
      });
    },

    handleFocus (event) {
      if (!this.softFocus) {
        if (this.automaticDropdown || this.filterable) {
          this.visible = true;
          this.menuVisibleOnFocus = true;
        }
        this.$emit('focus', event);
      } else {
        this.softFocus = false;
      }
    },

    blur () {
      this.visible = false;
      this.$refs.reference.blur();
    },
    // 【原生input】单选框失焦
    handleRadioBlur () {
      setTimeout(() => {
        if (!isNilOrEmptyString(this.radioValue)) {
          this.radioValue = '';
          this.visible = false;
        }
      }, 300)
    },

    // 【el-input】 失去焦点
    handleBlur (event) {
      setTimeout(() => {
        if (this.isSilentBlur) {
          this.isSilentBlur = false;
        } else {
          this.$emit('blur', event);
        }
      }, 50);
      this.softFocus = false;
    },

    handleClearClick (event) {
      this.deleteSelected(event);
    },

    doDestroy () {
      this.$refs.popper && this.$refs.popper.doDestroy();
    },

    handleClose () {
      this.visible = false;
    },

    toggleLastOptionHitState (hit) {
      if (!Array.isArray(this.selected)) return;
      const option = this.selected[this.selected.length - 1];
      if (!option) return;

      if (hit === true || hit === false) {
        option.hitState = hit;
        return hit;
      }

      option.hitState = !option.hitState;
      return option.hitState;
    },
    /**
     * 通过backspace键删除时、判断是单选还是多选删除
     */
    deletePrevTag (e) {
      if (this.isPicklist) {
        if (!isNilOrEmptyString(this.tagValue)) {
          if (!this.radioValue) {
            this.tagValue = null;
            // 单选调用父组件的clear方法
            this.$parent.clear();
            this.$refs.reference.focus();
          }
        }
      } else {
        if (e.target.value.length <= 0 && !this.toggleLastOptionHitState()) {
          const value = this.value.slice();
          value.pop();
          this.$emit('input', value);
          this.emitChange(value);
        }
      }
    },

    managePlaceholder () {
      if (this.currentPlaceholder !== '') {
        this.currentPlaceholder = this.$refs.input.value
          ? ''
          : this.cachedPlaceHolder;
      }
    },

    resetInputState (e) {
      if (e.keyCode !== 8) this.toggleLastOptionHitState(false);
      this.inputLength = this.$refs.input.value.length * 15 + 20;
      this.resetInputHeight();
    },

    resetInputHeight () {
      if (!this.multiple) return;
      this.$nextTick(() => {
        if (!this.$refs.reference) return;
        let inputChildNodes = this.$refs.reference.$el.childNodes;
        let input = [].filter.call(
          inputChildNodes,
          item => item.tagName === 'INPUT'
        )[0];
        const tags = this.$refs.tags;
        const sizeInMap = this.initialInputHeight || 35;

        if (this.selected.length === 0) {
          if (this.elForm.$parent.border) {
            input.style.height = '35px';
          } else {
            input.style.height = sizeInMap + 'px';
          }
        } else {
          let e = 0;
          if (tags) {
            e = tags.clientHeight + (tags.clientHeight > sizeInMap ? 3 : 0);
          }
          input.style.height = Math.max(e, sizeInMap) + 'px';
        }
        if (this.visible && this.emptyText !== false) {
          this.broadcast('ElSelectDropdown', 'updatePopper');
        }
      });
    },

    resetHoverIndex () {
      setTimeout(() => {
        if (!this.multiple) {
          this.hoverIndex = this.options.indexOf(this.selected);
        } else {
          if (this.selected.length > 0) {
            this.hoverIndex = Math.min.apply(
              null,
              this.selected.map(item => this.options.indexOf(item))
            );
          } else {
            this.hoverIndex = -1;
          }
        }
      }, 300);
    },

    handleOptionSelect (option, byClick) {
      if (this.multiple) {
        // value是选中项的下标组成的数组
        const value = this.value.slice();
        const optionIndex = this.getValueIndex(value, option.value);
        if (optionIndex > -1) {
          value.splice(optionIndex, 1);
        } else if (
          this.multipleLimit <= 0 ||
          value.length < this.multipleLimit
        ) {
          value.push(option.value);
        }
        this.$emit('input', value);
        this.emitChange(value);
        if (option.created) {
          this.query = '';
          this.handleQueryChange('');
          this.inputLength = 20;
        }
        if (this.filterable) this.$refs.input.focus();
      } else {
        this.$emit('input', option.value);
        this.emitChange(option.value);
        this.visible = false;
      }
      this.isSilentBlur = byClick;
      this.setSoftFocus();
      if (this.visible) return;
      this.$nextTick(() => {
        this.scrollToOption(option);
      });
    },

    setSoftFocus () {
      this.softFocus = true;
      const input = this.$refs.input || this.$refs.reference;
      if (input) {
        input.focus();
      }
    },

    getValueIndex (arr = [], value) {
      const isObject =
        Object.prototype.toString.call(value).toLowerCase() ===
        '[object object]';
      if (!isObject) {
        return arr.indexOf(value);
      } else {
        const valueKey = this.valueKey;
        let index = -1;
        arr.some((item, i) => {
          if (
            getValueByPath(item, valueKey) === getValueByPath(value, valueKey)
          ) {
            index = i;
            return true;
          }
          return false;
        });
        return index;
      }
    },

    toggleMenu () {
      if (!this.selectDisabled) {
        if (this.menuVisibleOnFocus) {
          this.menuVisibleOnFocus = false;
        } else {
          this.visible = !this.visible;
        }
        if (this.visible) {
          (this.$refs.input || this.$refs.reference).focus();
        }
      }
    },

    selectOption () {
      if (!this.visible) {
        this.toggleMenu();
      } else {
        if (this.options[this.hoverIndex]) {
          this.handleOptionSelect(this.options[this.hoverIndex]);
        }
      }
    },

    deleteSelected (event) {
      event.stopPropagation();
      const value = this.multiple ? [] : '';
      this.$emit('input', value);
      this.emitChange(value);
      this.visible = false;
      this.$emit('clear');
    },

    deleteTag (event, tag) {
      let index = this.selected.indexOf(tag);
      if (index > -1 && !this.selectDisabled) {
        const value = this.value.slice();
        value.splice(index, 1);
        this.$emit('input', value);
        this.emitChange(value);
        this.$emit('remove-tag', tag.value);
      }
      event.stopPropagation();
    },

    onInputChange () {
      if (this.filterable && this.query !== this.selectedLabel) {
        this.query = this.selectedLabel;
        this.handleQueryChange(this.query);
      }
    },

    onOptionDestroy (index) {
      if (index > -1) {
        this.optionsCount--;
        this.filteredOptionsCount--;
        this.options.splice(index, 1);
      }
    },

    resetInputWidth () {
      this.inputWidth = this.$refs.reference.$el.getBoundingClientRect().width;
    },

    checkDefaultFirstOption () {
      this.hoverIndex = -1;
      // highlight the created option
      let hasCreated = false;
      for (let i = this.options.length - 1; i >= 0; i--) {
        if (this.options[i].created) {
          hasCreated = true;
          this.hoverIndex = i;
          break;
        }
      }
      if (hasCreated) return;
      for (let i = 0; i !== this.options.length; ++i) {
        const option = this.options[i];
        if (this.query) {
          // highlight first options that passes the filter
          if (!option.disabled && !option.groupDisabled && option.visible) {
            this.hoverIndex = i;
            break;
          }
        } else {
          // highlight currently selected option
          if (option.itemSelected) {
            this.hoverIndex = i;
            break;
          }
        }
      }
    },

    getValueKey (item) {
      if (
        Object.prototype.toString.call(item.value).toLowerCase() !==
        '[object object]'
      ) {
        return item.value;
      } else {
        return getValueByPath(item.value, this.valueKey);
      }
    },

    onTagClick (item, text) {
      this.$emit('click', item, text);
    },
    del (index) {
      if (this.computedDisabled) {
        return;
      }
      this.selected.splice(index, 1);
      this.$emit('input', this.selected);
      this.$emit('confirm', this.selected);
    },
    multipleTagsMouseenterHandler () {
      this.inputIsHover = true;
    },
    multipleTagsMouseleaveHandler () {
      this.inputIsHover = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// select.vue?297f234c